/* eslint-disable no-console */

// A fresh request can distinguish a missing chunk, an HTML fallback, and a network
// failure. It is a probe of the current response, not the original script request.
const probeChunkResponse = async (chunkURL, context) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), 10000);

  try {
    const response = await fetch(chunkURL.href, {
      cache: 'no-store',
      credentials: 'same-origin',
      signal: controller.signal,
    });
    const contentType = response.headers.get('content-type');

    console.error('[Dashboard] Page bundle response probe', {
      ...context,
      chunkRequest: chunkURL.href,
      status: response.status,
      contentType,
      isHTML: /\btext\/html\b|\bapplication\/xhtml\+xml\b/i.test(contentType || ''),
      redirected: response.redirected,
      responseURL: response.url,
      cacheControl: response.headers.get('cache-control'),
      age: response.headers.get('age'),
      etag: response.headers.get('etag'),
      server: response.headers.get('server'),
      xCache: response.headers.get('x-cache'),
      via: response.headers.get('via'),
    });
  } catch (error) {
    console.warn('[Dashboard] Page bundle response probe failed', {
      ...context,
      chunkRequest: chunkURL.href,
      name: error.name,
      message: error.message,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
};

export const logPageLoadFailure = async (error, { timedOut = false, retryCount = 0 } = {}) => {
  const context = {
    pathname: window.location.pathname,
    retryCount,
  };

  console.error('[Dashboard] Page bundle failed to load', {
    ...context,
    timedOut,
    online: navigator.onLine,
    name: error?.name,
    message: error?.message,
    type: error?.type,
    chunkRequest: error?.request,
    stack: error?.stack,
    // Hashed script names identify the build currently running in the browser.
    loadedScripts: Array.from(document.scripts, (script) => script.src).filter(Boolean),
  });

  if (!error?.request) {
    return;
  }

  let chunkURL;
  try {
    chunkURL = new URL(error.request, window.location.origin);
  } catch {
    return;
  }

  // Only probe our own static bundles; never probe API requests or external URLs.
  if (
    chunkURL.origin !== window.location.origin ||
    !/^\/static\/(js|css)\/.+\.(js|css)$/.test(chunkURL.pathname) ||
    chunkURL.search ||
    chunkURL.hash
  ) {
    return;
  }

  await probeChunkResponse(chunkURL, context);
};
